import { Component, OnInit } from '@angular/core';

import { ModalWrapperComponent } from '../../UI-Lib/Modal-Wrapper/modalWrapper.component';
import { IWeissmanModalComponent } from '../../Compliance/WeissmanModalService';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DescriptorValidationComponent } from './Validation/descriptorValidation.component';
import { ToastrService } from 'ngx-toastr';
import { DescriptorService } from '../descriptor.service';

export interface PropertyCharacteristicDetailParams {
    descriptor: Core.DescriptorDTO;
    usages: Weissman.Model.Descriptors.DescriptorUsage[];
    assetUsages: Weissman.Model.Descriptors.DescriptorUsage[];
    aRCCUsages: Weissman.Model.Descriptors.DescriptorUsage[];
    fieldTypes: Weissman.Model.Descriptors.DescriptorFieldType[];
    hasComplianceFeatures: boolean;
    isNew: boolean;
}

@Component({
    selector: 'property-characteristic-detail',
    templateUrl: './propertyCharacteristicDetail.component.html',
    imports: [ModalWrapperComponent, ReactiveFormsModule, DescriptorValidationComponent],
})
export class PropertyCharacteristicDetailComponent
    implements OnInit, IWeissmanModalComponent<PropertyCharacteristicDetailParams, Core.DescriptorDTO>
{
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _descriptorService: DescriptorService,
        private readonly _fb: FormBuilder,
        private readonly _toastr: ToastrService,
    ) {}

    params: PropertyCharacteristicDetailParams;
    result: Core.DescriptorDTO;

    descriptor: Core.DescriptorDTO;

    descriptorForm: FormGroup;
    usages: Weissman.Model.Descriptors.DescriptorUsage[];
    assetUsages: Weissman.Model.Descriptors.DescriptorUsage[];
    fieldTypes: Weissman.Model.Descriptors.DescriptorFieldType[];
    hasComplianceFeatures: boolean;

    saving: boolean;

    get validationForm(): FormGroup {
        return this.descriptorForm.get('validation') as FormGroup;
    }

    ngOnInit(): void {
        this.hasComplianceFeatures = this.params.hasComplianceFeatures;

        this.usages = this.params.usages;
        this.assetUsages = this.params.assetUsages;
        this.fieldTypes = this.params.fieldTypes;

        this.descriptor = this.params.descriptor;

        this.descriptorForm = this._fb.group({
            name: [this.descriptor?.name ?? '', [Validators.maxLength(25), Validators.required]],
            companyUsageId: [this.descriptor?.companyUsageId ?? null, [Validators.required]],
            siteUsageID: [this.descriptor?.siteUsageID ?? null, [Validators.required]],
            parcelUsageID: [this.descriptor?.parcelUsageID ?? null, [Validators.required]],
            assessorUsageID: [this.descriptor?.assessorUsageID ?? null, [Validators.required]],
            assetUsageID: [this.descriptor?.assetUsageID ?? null, [Validators.required]],
            arccUsageID: [this.descriptor?.arccUsageID ?? null],
            fieldTypeID: [this.descriptor?.fieldTypeID ?? null],
            notes: [this.descriptor?.notes ?? '', [Validators.maxLength(200)]],
            enabled: [this.descriptor?.enabled ?? true],
            formatID: [this.descriptor?.formatID ?? null],
            pickListGroupID: [this.descriptor?.pickListGroupID ?? null],
            validation: this._fb.group({
                minValue: [this.descriptor?.validation?.minValue ?? null],
                maxValue: [this.descriptor?.validation?.maxValue ?? null],
                maxLength: [this.descriptor?.validation?.maxLength ?? null],
                limitToList: [this.descriptor?.validation?.limitToList ?? false],
            }),
        });

        this.descriptorForm.get('fieldTypeID').valueChanges.subscribe(() => {
            const fieldTypeID = this.descriptorForm.get('fieldTypeID').value;
            if (fieldTypeID === this.descriptor?.fieldTypeID) {
                this.validationForm.reset({
                    minValue: this.descriptor?.validation?.minValue ?? null,
                    maxValue: this.descriptor?.validation?.maxValue ?? null,
                    maxLength: this.descriptor?.validation?.maxLength ?? null,
                    limitToList: this.descriptor?.validation?.limitToList ?? false,
                });
            } else {
                this.validationForm.reset({
                    minValue: null,
                    maxValue: null,
                    maxLength: null,
                    limitToList: false,
                });
            }
        });
    }

    async save(): Promise<void> {
        this.saving = true;

        const message = this._getValidationError();

        if (message) {
            this._toastr.error(message);
            return;
        }

        try {
            const formValues = this.descriptorForm.getRawValue();
            const newDescriptor = {
                ...this.params.descriptor,
                ...formValues,
            };

            if (this.params.descriptor.descriptorID) {
                await this._descriptorService.update(newDescriptor);
            } else {
                await this._descriptorService.create(newDescriptor);
            }
            this.result = newDescriptor;
            this._bsModalRef.hide();
        } finally {
            this.saving = false;
        }
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    private _getValidationError(): string {
        const thisFormValues = this.descriptorForm.get('validation').getRawValue();
        if (thisFormValues.maxValue && thisFormValues.minValue && thisFormValues.maxValue < thisFormValues.minValue) {
            return 'Min Value must be less than Max!';
        }

        if (thisFormValues.maxValue < 0 || thisFormValues.minValue < 0 || thisFormValues.maxLength < 0) {
            return 'Value cannot be negative!';
        }

        return '';
    }
}
